import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { Avatar, Button, Dropdown, Modal, RoleBadge, accountRole } from '@core/ui';
import type { DropdownItem } from '@core/ui';
import { logout, selectCurrentUser, useLogoutMutation } from '@features/auth';
import { useActiveOrganization } from '@features/organization';

/**
 * Who is signed in, and the three things they can do about it.
 *
 * The header used to spell the email out and put a logout button next to it,
 * which spent the width of a phone screen saying something nobody reads twice.
 * The avatar says it in 40px, the menu says the rest: the full name, the address,
 * what this person may do in this organization, and the way to Ajustes, to Equipo
 * (only for an admin, because that is the only person the screen would obey) and
 * out.
 *
 * Signing out still goes through a confirmation dialog: it ends the session on
 * every tab, and the design system asks for a `Modal` on anything that does.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const { role, isAdmin } = useActiveOrganization();
  const [serverLogout, { isLoading: loggingOut }] = useLogoutMutation();
  const [confirming, setConfirming] = useState(false);

  if (!user) return null;

  const name = user.full_name || user.email;

  const handleLogout = async () => {
    // Best-effort server-side token revocation before clearing client state
    // (the request still carries the token at this point).
    try {
      await serverLogout().unwrap();
    } catch {
      /* ignore — log out locally regardless */
    }
    setConfirming(false);
    dispatch(logout());
    navigate('/login');
  };

  const items: DropdownItem[] = [
    {
      key: 'settings',
      label: t('userMenu.settings'),
      icon: 'heroicons:cog-6-tooth',
      onSelect: () => navigate('/ajustes'),
    },
    ...(isAdmin
      ? [
          {
            key: 'audit',
            label: t('audit.title'),
            icon: 'heroicons:clipboard-document-list',
            onSelect: () => navigate('/ajustes/auditoria'),
          },
          {
            key: 'team',
            label: t('userMenu.team'),
            icon: 'heroicons:users',
            onSelect: () => navigate('/ajustes/equipo'),
          },
        ]
      : []),
    {
      key: 'logout',
      label: t('nav.logout'),
      icon: 'heroicons:arrow-right-on-rectangle',
      tone: 'danger',
      onSelect: () => setConfirming(true),
    },
  ];

  return (
    <>
      <Dropdown
        label={t('userMenu.label')}
        items={items}
        className="gap-x-2 px-1.5 sm:px-2.5"
        header={
          <span className="block">
            <span className="block truncate text-sm font-medium text-[var(--color-text)]">{name}</span>
            <span className="block truncate text-xs text-[var(--color-text-secondary)]">{user.email}</span>
            <RoleBadge role={accountRole(user.is_superuser, role)} className="mt-1.5" />
          </span>
        }
      >
        <Avatar name={name} />
        {/* The name and the chevron are the desktop's affordance; on a phone the
            avatar alone has to earn its 40px, and the header has none to spare. */}
        <span className="hidden min-w-0 flex-col items-start md:flex">
          <span className="max-w-[140px] truncate text-sm font-medium text-[var(--color-text)]">{name}</span>
          <span className="max-w-[140px] truncate text-xs text-[var(--color-text-secondary)]">{user.email}</span>
        </span>
        <Icon icon="heroicons:chevron-down" aria-hidden="true" className="hidden size-4 md:inline" />
      </Dropdown>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t('logout.title')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={loggingOut}>
              {t('common.cancel')}
            </Button>
            {/* `danger` is the one signal that says this ends the session: it colours
                the button AND stops Enter from pressing it, so signing out is always
                a deliberate click. */}
            <Button variant="danger" size="sm" onClick={handleLogout} disabled={loggingOut}>
              {t('logout.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-text-secondary)]">{t('logout.body', { email: user.email })}</p>
      </Modal>
    </>
  );
}
